import React, { memo, useMemo } from 'react';
import clsx from 'clsx';
import { Grid, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller } from 'react-hook-form';

import {
  DELAY_WAVE_CUT_OFF_TIMES,
  DELAY_DEPART_TIMES,
  CONFIRM,
  SWITCH_TO_DISASTER,
} from '../../../constants/strings';
import Button from '../../../components/Button';
import palette from '../../../components/styles/palette';

const useStyles = makeStyles(({ typography }) => ({
  inputLabel: {
    fontWeight: 'bold',
  },
  row: {
    minHeight: typography.pxToRem(65),
  },
  buttonContainer: {
    marginTop: typography.pxToRem(50),
    justifyContent: 'space-between',
  },
  blueButton: {
    backgroundColor: palette.btn.blueFill,
    border: palette.btn.blueBorder,
    '&:hover': {
      backgroundColor: palette.btn.blueFill,
    },
  },
  redButton: {
    backgroundColor: palette.dpdRed,
    border: palette.dpdRed,
    '&:hover': {
      backgroundColor: palette.dpdRed,
    },
  },
  hidden: {
    visibility: 'hidden',
  },
}));

const DisasterRecoveryForm = ({
  values,
  control,
  errors,
  handleConfirm,
  handleSwitchToDisasterRecoveryMode,
  isDRmode,
  dirty,
  isValid,
  initialValues,
}) => {
  const style = useStyles();

  const isConfirmDisable = useMemo(() => {
    const { waveCutOff, departTime } = values;
    return !(
      dirty &&
      isValid &&
      ((waveCutOff && initialValues.waveCutOff !== waveCutOff) ||
        (departTime && initialValues.departTime !== departTime))
    );
  }, [dirty, isValid, values, initialValues]);

  return (
    <>
      <Grid container spacing={2} className={style.row}>
        <Grid item xs={8}>
          <label
            className={style.inputLabel}
            htmlFor="waveCutOff"
          >{`${DELAY_WAVE_CUT_OFF_TIMES}:`}</label>
        </Grid>
        <Grid item xs={4}>
          <Controller
            name="waveCutOff"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="waveCutOff"
                error={!!errors.waveCutOff}
                helperText={errors.waveCutOff}
                type="text"
                variant="outlined"
                data-testid="wave-cut-off"
                value={values.waveCutOff}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={style.row}>
        <Grid item xs={8}>
          <label
            className={style.inputLabel}
            htmlFor="departTime"
          >{`${DELAY_DEPART_TIMES}:`}</label>
        </Grid>
        <Grid item xs={4}>
          <Controller
            name="departTime"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                error={!!errors.departTime}
                helperText={errors.departTime}
                id="departTime"
                type="text"
                variant="outlined"
                data-testid="depart-time"
                value={values.departTime}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container className={style.buttonContainer}>
        <Button
          minWidthZero
          onClick={handleSwitchToDisasterRecoveryMode}
          className={clsx(style.redButton, { [style.hidden]: isDRmode })}
          opacityDisabled={0.5}
          data-testid="disaster-recovery-button"
        >
          {SWITCH_TO_DISASTER}
        </Button>
        <Button
          minWidthZero
          onClick={handleConfirm}
          className={style.blueButton}
          bgColorDisabled={palette.btn.blueFill}
          opacityDisabled={0.5}
          data-testid="confirm-button"
          disabled={isConfirmDisable}
        >
          {CONFIRM}
        </Button>
      </Grid>
    </>
  );
};

export default memo(DisasterRecoveryForm);
